import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './OJTCoordinator.css';
import { FaHome, FaChartLine, FaUser, FaUsers, FaMapMarkerAlt } from 'react-icons/fa';
import { FaArrowRightFromBracket } from 'react-icons/fa6';
import { useAuth } from '../../lib/AuthProvider';
import TraineeList from './Trainees/TraineeList';
import { supabase } from '../../lib/supabaseClient';
import { StudentProfile } from '../../lib/database.types';
import { TotalStudentsCard, TotalStudentsModal } from './ProgramStat/TotalStudents';
import { ActiveStudentsCard, ActiveStudentsModal } from './ProgramStat/ActiveStudents';
import { AtRiskStudentsCard, AtRiskStudentsModal } from './ProgramStat/AtRiskStudents';
import { ClassSectionsCard, ClassSectionsModal } from './ProgramStat/ClassSections';

interface WeeklyAttendanceSummary {
  id: string;
  student_id: string;
  week_number: number;
  scheduled_days: number;
  days_present: number;
  days_absent: number;
  noclass_days: number;
  avg_hours_per_day: number;
  late_minutes: number;
  created_at: string;
  updated_at: string;
}

interface WeeklyTrainee {
  name: string;
  department: string;
  weeklyHours: number;
  status: 'At Risk' | 'Not At Risk';
  riskScore?: number;
  attendanceRate?: number;
  absentRate?: number;
  holidayRatio?: number;
}

const OJTCoordinatorHome: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [userName, setUserName] = useState({ firstName: '', lastName: '' });
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<'home' | 'trainees'>('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [showActiveStudentsModal, setShowActiveStudentsModal] = useState(false);
  const [showClassSectionsModal, setShowClassSectionsModal] = useState(false);
  const [showAtRiskStudentsModal, setShowAtRiskStudentsModal] = useState(false);
  const [students, setStudents] = useState<StudentProfile[]>([]);
  const [studentsWithStatus, setStudentsWithStatus] = useState<Array<StudentProfile & { status: 'Active' | 'Inactive', lastCheckIn?: string }>>([]);
  const [studentsBySection, setStudentsBySection] = useState<{ [section: string]: StudentProfile[] }>({});
  const [weeklyTrainees, setWeeklyTrainees] = useState<WeeklyTrainee[]>([]);
  const [editingStudentId, setEditingStudentId] = useState<string | null>(null);
  const [editingDepartment, setEditingDepartment] = useState<string>('');
  const [editingSupervisor, setEditingSupervisor] = useState<string>('');
  const [offices, setOffices] = useState<string[]>([]);
  const [officesLoading, setOfficesLoading] = useState(false);
  const [stats, setStats] = useState({
    totalStudents: 0,
    activeStudents: 0,
    atRiskStudents: 0,
    totalHours: 0
  });

  // Function to call the risk prediction API
  const predictRisk = async (weeklyData: WeeklyAttendanceSummary): Promise<{risk_label: string, attendance_rate: number, absent_rate: number, holiday_ratio: number}> => {
    try {
      const response = await fetch('http://localhost:5000/predict', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          weekly_data: {
            scheduled_days: weeklyData.scheduled_days,
            days_present: weeklyData.days_present,
            days_absent: weeklyData.days_absent,
            noclass_days: weeklyData.noclass_days,
            avg_hours_per_day: weeklyData.avg_hours_per_day,
            late_minutes: weeklyData.late_minutes
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error calling risk prediction API:', error);
      // Return default values if API call fails
      return {
        risk_label: '0',
        attendance_rate: 0,
        absent_rate: 0,
        holiday_ratio: 0
      };
    }
  };

  // Fetch all students
  const fetchStudents = async () => {
    try {
      const { data: studentsData, error } = await supabase
        .from('student_profile')
        .select('*')
        .order('last_name', { ascending: true });

      if (error) throw error;

      setStudents(studentsData || []);
      return studentsData?.length || 0;
    } catch (error) {
      console.error('Error fetching students:', error);
      return 0;
    }
  };

  // Fetch weekly trainees with risk prediction
  const fetchWeeklyTrainees = async () => {
    try {
      // Fetch all student profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('student_profile')
        .select('*');

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        return 0;
      }

      if (!profiles || profiles.length === 0) {
        setWeeklyTrainees([]);
        return 0;
      }

      // Fetch weekly attendance summary for all students
      const { data: weeklyAttendance, error: weeklyError } = await supabase
        .from('weekly_attendance_summary')
        .select('*')
        .order('week_number', { ascending: false });

      if (weeklyError) {
        console.error('Error fetching weekly attendance:', weeklyError);
      }

      // Create weekly trainees list with risk prediction
      const weeklyTraineesData: WeeklyTrainee[] = await Promise.all(
        profiles.map(async (profile: StudentProfile) => {
          // Find the most recent weekly attendance data for this student
          const studentWeeklyData = weeklyAttendance?.find(
            (attendance: WeeklyAttendanceSummary) => attendance.student_id === profile.student_id
          ) as WeeklyAttendanceSummary | undefined;

          if (studentWeeklyData) {
            // Get risk prediction
            const riskPrediction = await predictRisk(studentWeeklyData);
            
            // Calculate weekly hours from avg_hours_per_day * days_present
            const weeklyHours = (studentWeeklyData as WeeklyAttendanceSummary).avg_hours_per_day * (studentWeeklyData as WeeklyAttendanceSummary).days_present;
            
            return {
              name: `${profile.first_name} ${profile.last_name}`,
              department: profile.department,
              weeklyHours: Math.round(weeklyHours * 10) / 10,
              status: riskPrediction.risk_label === '1' ? 'At Risk' : 'Not At Risk',
              riskScore: parseFloat(riskPrediction.risk_label),
              attendanceRate: riskPrediction.attendance_rate,
              absentRate: riskPrediction.absent_rate,
              holidayRatio: riskPrediction.holiday_ratio
            };
          } else {
            // Fallback to mock data if no weekly attendance data
            const weeklyHours = Math.random() * 40 + 20;
            return {
              name: `${profile.first_name} ${profile.last_name}`,
              department: profile.department,
              weeklyHours: Math.round(weeklyHours * 10) / 10,
              status: weeklyHours < 30 ? 'At Risk' : 'Not At Risk'
            };
          }
        })
      );

      setWeeklyTrainees(weeklyTraineesData);
      
      // Count at-risk students
      const atRiskCount = weeklyTraineesData.filter(t => t.status === 'At Risk').length;
      return atRiskCount;
    } catch (error) {
      console.error('Error fetching weekly trainees:', error);
      return 0;
    }
  };

  // Fetch offices from office_deployment table
  const fetchOffices = async () => {
    setOfficesLoading(true);
    try {
      const { data, error } = await supabase
        .from('office_deployment')
        .select('office')
        .order('office', { ascending: true });

      if (error) {
        console.error('Error fetching offices:', error);
        return;
      }

      // Extract unique office names
      if (data && data.length > 0) {
        const officeNames = data.map((item: any) => item.office).filter((office: any) => office);
        const uniqueOffices = [...new Set(officeNames)];
        setOffices(uniqueOffices);
      }
    } catch (error) {
      console.error('Error fetching offices:', error);
    } finally {
      setOfficesLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (user && 'first_name' in user && 'last_name' in user) {
          setUserName({
            firstName: user.first_name,
            lastName: user.last_name
          });
        } else {
          const storedProfile = JSON.parse(localStorage.getItem('userProfile') || '{}');
          if (storedProfile.first_name) {
            setUserName({
              firstName: storedProfile.first_name,
              lastName: storedProfile.last_name
            });
          }
        }
        
        const totalStudents = await fetchStudents();
        const activeStudents = await fetchStudentsWithStatus();
        const classSections = await fetchStudentsBySection();
        const atRiskStudents = await fetchWeeklyTrainees();
        
        setStats({
          totalStudents: totalStudents,
          activeStudents: activeStudents,
          atRiskStudents: atRiskStudents,
          totalHours: classSections,
        });
        
        // Fetch offices for department management
        await fetchOffices();
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Fetch students with their active/inactive status
  const fetchStudentsWithStatus = async () => {
    try {
      const { data: studentsData, error: studentsError } = await supabase
        .from('student_profile')
        .select('*')
        .order('last_name', { ascending: true });

      if (studentsError) throw studentsError;

      // Get today's date in YYYY-MM-DD format
      const today = new Date().toISOString().split('T')[0];

      // Fetch all check-ins for today
      const { data: checkInsData, error: checkInsError } = await supabase
        .from('check_ins')
        .select('user_id, check_time')
        .gte('check_date', today)
        .lte('check_date', today);

      if (checkInsError) throw checkInsError;

      // Create a map of user_id to their latest check-in time
      const checkInMap = new Map<string, string>();
      (checkInsData || []).forEach((checkIn: any) => {
        const existingTime = checkInMap.get(checkIn.user_id);
        if (!existingTime || checkIn.check_time > existingTime) {
          checkInMap.set(checkIn.user_id, checkIn.check_time);
        }
      });

      // Map students with their status
      const studentsWithStatusData = (studentsData || []).map((student: StudentProfile) => {
        const hasCheckedInToday = checkInMap.has(student.id);
        return {
          ...student,
          status: hasCheckedInToday ? 'Active' : 'Inactive',
          lastCheckIn: checkInMap.get(student.id)
        } as StudentProfile & { status: 'Active' | 'Inactive', lastCheckIn?: string };
      });

      setStudentsWithStatus(studentsWithStatusData);

      // Count active students
      const activeCount = studentsWithStatusData.filter(s => s.status === 'Active').length;
      
      return activeCount;
    } catch (error) {
      console.error('Error fetching students with status:', error);
      return 0;
    }
  };

  // Handle opening the students modal
  const handleViewStudents = () => {
    setShowStudentsModal(true);
  };

  // Handle opening the active students modal
  const handleViewActiveStudents = async () => {
    await fetchStudentsWithStatus();
    setShowActiveStudentsModal(true);
  };

  // Fetch and group students by class section
  const fetchStudentsBySection = async () => {
    try {
      const { data: studentsData, error } = await supabase
        .from('student_profile')
        .select('*')
        .order('course_info', { ascending: true })
        .order('last_name', { ascending: true });

      if (error) throw error;

      // Group students by course_info (class section)
      const groupedStudents: { [section: string]: StudentProfile[] } = {};
      
      (studentsData || []).forEach((student: StudentProfile) => {
        const section = student.course_info || 'Unassigned';
        if (!groupedStudents[section]) {
          groupedStudents[section] = [];
        }
        groupedStudents[section].push(student);
      });

      setStudentsBySection(groupedStudents);
      return Object.keys(groupedStudents).length;
    } catch (error) {
      console.error('Error fetching students by section:', error);
      return 0;
    }
  };

  // Handle opening the class sections modal
  const handleViewClassSections = async () => {
    await fetchStudentsBySection();
    setShowClassSectionsModal(true);
  };

  // Handle opening the at-risk students modal
  const handleViewAtRiskStudents = async () => {
    // Refresh the data to ensure it's current
    await fetchWeeklyTrainees();
    setShowAtRiskStudentsModal(true);
  };

  // Handle starting edit mode
  const handleEditDepartment = (student: StudentProfile) => {
    setEditingStudentId(student.id);
    setEditingDepartment(student.department || '');
    setEditingSupervisor(student.supervisor || '');
  };

  // Handle saving department and supervisor
  const handleSaveDepartment = async (student: StudentProfile) => {
    try {
      // Update student's department and supervisor in the database
      const { error }: any = await (supabase as any)
        .from('student_profile')
        .update({ 
          department: editingDepartment,
          supervisor: editingSupervisor 
        })
        .eq('id', student.id);

      if (error) throw error;

      // Update local state for both students lists
      setStudents(students.map(s => 
        s.id === student.id 
          ? { ...s, department: editingDepartment, supervisor: editingSupervisor }
          : s
      ));

      // Also update studentsWithStatus if it exists
      setStudentsWithStatus(studentsWithStatus.map(s => 
        s.id === student.id 
          ? { ...s, department: editingDepartment, supervisor: editingSupervisor }
          : s
      ));

      setEditingStudentId(null);
      setEditingDepartment('');
      setEditingSupervisor('');
      
      // Show success message
      alert('Department and supervisor updated successfully!');
    } catch (error) {
      console.error('Error saving department and supervisor:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    setEditingStudentId(null);
    setEditingDepartment('');
    setEditingSupervisor('');
  };

  // Sample data for pending tasks
  const pendingTasks = [
    { id: 1, name: 'Approve Student Registration', student: 'Maria Santos', date: '2025-09-08', status: 'pending' },
    { id: 2, name: 'Verify Weekly Report', student: 'John Dela Cruz', date: '2025-09-07', status: 'pending' },
    { id: 3, name: 'Review Completed Hours', student: 'Ana Reyes', date: '2025-09-06', status: 'completed' },
    { id: 4, name: 'Approve Location Change', student: 'Mike Rodriguez', date: '2025-09-05', status: 'pending' },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/supervisor');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="coordinator-home">
      <header className="top-nav">
        <div className="nav-container">
          <div className="logo">
            <h1>InternTr<FaMapMarkerAlt className="location-icon" />ckr</h1>
          </div>
          <button
            className="hamburger-btn"
            aria-label="Open menu"
            aria-controls="ojt-sidebar"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(true)}
          >
            {'\u2630'}
          </button>
          <div className="nav-links">
            <a 
              href="#" 
              className={`nav-link ${activeView === 'home' ? 'active' : ''}`}
              onClick={() => setActiveView('home')}
            >
              <FaHome className="nav-icon" /> Home
            </a>
            <a 
              href="#" 
              className={`nav-link ${activeView === 'trainees' ? 'active' : ''}`}
              onClick={() => setActiveView('trainees')}
            >
              <FaUsers className="nav-icon" /> Trainees
            </a>
            <a href="#" className="nav-link"><FaChartLine className="nav-icon" /> Analytics</a>
            <a href="#" className="nav-link"><FaUser className="nav-icon" /> Profile</a>
          </div>
          <button onClick={handleSignOut} className="nav-link logout-button"><FaArrowRightFromBracket className="nav-icon" /> Sign Out</button>
        </div>
      </header>

      {/* Mobile sidebar and backdrop */}
      <div
        className={`backdrop${isMenuOpen ? ' open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden={!isMenuOpen}
      />
      <aside
        id="ojt-sidebar"
        className={`sidebar${isMenuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="sidebar-header">
          <h2>Menu</h2>
          <button
            className="close-btn"
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          >
            {'\u00D7'}
          </button>
        </div>
        <nav className="sidebar-links" onClick={() => setIsMenuOpen(false)}>
          <a 
            href="#" 
            className="nav-link"
            onClick={() => setActiveView('home')}
          >
            <FaHome className="nav-icon" /> Home
          </a>
          <a 
            href="#" 
            className="nav-link"
            onClick={() => setActiveView('trainees')}
          >
            <FaUsers className="nav-icon" /> Trainees
          </a>
          <a href="#" className="nav-link"><FaChartLine className="nav-icon" /> Analytics</a>
          <a href="#" className="nav-link"><FaUser className="nav-icon" /> Profile</a>
          <button onClick={handleSignOut} className="nav-link logout-button"><FaArrowRightFromBracket className="nav-icon" /> Sign Out</button>
        </nav>
      </aside>

      <main className="main-content">
        {activeView === 'home' ? (
          <>
            <div className="welcome-section">
              <h3 className="coordinator-title">OJT Coordinator Dashboard</h3>
              <h1>Welcome, {loading ? "Loading..." : `${userName.firstName} ${userName.lastName}`}</h1>
              <p className="welcome-message">
                As an OJT Coordinator, you play a crucial role in shaping the future of our interns. 
                Your guidance helps bridge academic learning with real-world experience, 
                preparing our students for successful careers. Today, you have {stats.atRiskStudents} at-risk 
                students that require your attention.
              </p>
            </div>
            
            <div className="dashboard-grid">
              <div className="panel">
                <div className="panel-header">Program Statistics</div>
                <div className="panel-content">
                  <div className="stats-grid">
                    <TotalStudentsCard 
                      count={stats.totalStudents} 
                      onClick={handleViewStudents} 
                    />
                    <ActiveStudentsCard 
                      count={stats.activeStudents} 
                      onClick={handleViewActiveStudents} 
                    />
                    <AtRiskStudentsCard 
                      count={stats.atRiskStudents} 
                      onClick={handleViewAtRiskStudents} 
                    />
                    <ClassSectionsCard 
                      count={stats.totalHours} 
                      onClick={handleViewClassSections} 
                    />
                  </div>
                </div>
              </div>

              <div className="panel tasks-panel">
                <div className="panel-header">Tasks Requiring Attention</div>
                <div className="panel-content">
                  {pendingTasks.map(task => (
                    <div key={task.id} className="task-item">
                      <div className="task-info">
                        <div className="task-name">{task.name}</div>
                        <div className="task-details">Student: {task.student} • Date: {task.date}</div>
                      </div>
                      <div className={`task-status ${task.status === 'pending' ? 'status-pending' : 'status-completed'}`}>
                        {task.status === 'pending' ? 'Pending' : 'Completed'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="trainees-section">
            <h3 className="coordinator-title">Trainee Management</h3>
            <TraineeList />
          </div>
        )}
      </main>

      {/* Class Sections Modal */}
      <ClassSectionsModal
        isOpen={showClassSectionsModal}
        onClose={() => setShowClassSectionsModal(false)}
        studentsBySection={studentsBySection}
      />

      {/* Active Students Modal */}
      <ActiveStudentsModal
        isOpen={showActiveStudentsModal}
        onClose={() => setShowActiveStudentsModal(false)}
        studentsWithStatus={studentsWithStatus}
      />

      {/* Students Modal */}
      <TotalStudentsModal
        isOpen={showStudentsModal}
        onClose={() => setShowStudentsModal(false)}
        students={students}
        editingStudentId={editingStudentId}
        editingDepartment={editingDepartment}
        editingSupervisor={editingSupervisor}
        onEditDepartment={handleEditDepartment}
        onSaveDepartment={handleSaveDepartment}
        onCancelEdit={handleCancelEdit}
        onDepartmentChange={setEditingDepartment}
        onSupervisorChange={setEditingSupervisor}
        offices={offices}
        officesLoading={officesLoading}
      />

      {/* At Risk Students Modal */}
      <AtRiskStudentsModal
        isOpen={showAtRiskStudentsModal}
        onClose={() => setShowAtRiskStudentsModal(false)}
        weeklyTrainees={weeklyTrainees}
        loading={loading}
      />
    </div>
  );
};

export default OJTCoordinatorHome;
